import React from 'react';
import styled, { css } from 'styled-components';
import Imgix from 'shared/components/imgix';

import { DesktopOnly, MobileOnly } from 'shared/components';
import useUI from 'src/hooks/use-ui';
import { Typography } from 'src/typography';
import { ModalExitIcon } from 'src/components/core';
import { NotificationIcon } from 'src/assets/loyalty-pass/notification-icon';
import { RewardsBalanceIcon } from 'src/assets/loyalty-pass/rewards-balance-icon';
import { isIOS } from 'shared/helpers/utils';
import { mediaQueries } from 'shared/styles';
import { AppleWalletMobileButton } from '../loyalty/loyalty-pass/apple-wallet-button';
import { QRCode } from './qr-code';

type LoyaltyPassAddToWalletProps = {
  url: string;
  onClose: () => void;
  // The X icon; defaults to `onClose` so existing callers that only distinguish a single close
  // action keep behaving exactly as before.
  onDismiss?: () => void;
  headerText?: string;
  closeButtonText?: string;
  qrCodeSize?: number;
  includeIconRow?: boolean;
};

const APPLE_WALLET_BARCODE_URL = 'https://images.dutchie.com/pay/apple-wallet-barcode.png';

/**
 * Displays a loyalty pass download UI with responsive, OS-aware rendering:
 *
 * Desktop (all OS):
 *   - X close icon, QR code to scan and add pass
 *
 * Mobile iOS:
 *   - Apple Wallet barcode image and "Add to Apple Wallet" button
 *   (QR code hidden — direct wallet install is available via the button)
 *
 * Mobile non-iOS:
 *   - QR code to scan and add pass
 */
export const LoyaltyPassAddToWallet = ({
  url,
  onClose,
  onDismiss = onClose,
  headerText = 'Add loyalty pass to your wallet',
  closeButtonText = 'Close',
  qrCodeSize = 230,
  includeIconRow = false,
}: LoyaltyPassAddToWalletProps): JSX.Element => {
  const isIOSDevice = isIOS();
  const { isKiosk } = useUI();

  return (
    <Container>
      <DesktopOnly display='contents'>
        <ModalExitIcon onClick={onDismiss} />
      </DesktopOnly>
      <HeaderTitle id='loyalty-pass-title' size='small' tag='h2' weight='semi'>
        {headerText}
      </HeaderTitle>
      <ContentWrapper>
        {isIOSDevice && !isKiosk ? (
          <>
            <MobileOnly>
              <Imgix
                src={APPLE_WALLET_BARCODE_URL}
                htmlAttributes={{ alt: 'Apple Wallet barcode', style: { width: '100%' } }}
              />
            </MobileOnly>
            <DesktopOnly>
              <QRCode url={url} size={qrCodeSize} />
            </DesktopOnly>
          </>
        ) : (
          <QRCode url={url} size={qrCodeSize} />
        )}

        {includeIconRow && (
          <BenefitsRow>
            <BenefitItem>
              <BenefitIcon>
                <RewardsBalanceIcon />
              </BenefitIcon>
              <BenefitText size='large' tag='p' weight='regular'>
                Access your rewards balance anytime
              </BenefitText>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>
                <NotificationIcon />
              </BenefitIcon>
              <BenefitText size='large' tag='p' weight='regular'>
                Get notified of exclusive deals & events
              </BenefitText>
            </BenefitItem>
          </BenefitsRow>
        )}
      </ContentWrapper>
      <FooterContainer>
        {isIOSDevice && !isKiosk && (
          <MobileOnly>
            <AppleWalletMobileButton href={url} />
          </MobileOnly>
        )}

        <CloseButton onClick={onClose} aria-label='Close loyalty pass modal'>
          {closeButtonText}
        </CloseButton>
      </FooterContainer>
    </Container>
  );
};

const FooterContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[4]};
    width: 100%;
  `}
`;

const Container = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[6]};
    width: 100%;
  `}
`;

const HeaderTitle = styled(Typography.Heading)`
  align-self: stretch;
  line-height: 1.2;
  padding: ${({ theme: { spaces } }) => `0 ${spaces[6]}`};

  @media ${mediaQueries.largeTablet} {
    font-size: 20px;
    padding: 0;
    text-align: center;
  }
`;

const ContentWrapper = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[4]};
    align-self: stretch;
  `}
`;

const CloseButton = styled.button`
  ${({ theme: { colors } }) => css`
    background: none;
    border: none;
    color: ${colors.blue[50]};
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.065px;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  `}
`;

const BenefitsRow = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    padding-bottom: ${spaces[2]};
    justify-content: center;
    align-items: flex-start;
    gap: ${spaces[2]};
    align-self: stretch;
  `}
`;

const BenefitItem = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[2]};
  `}
`;

const BenefitIcon = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    display: flex;
    padding: ${spaces[2]};
    justify-content: center;
    align-items: center;
    border-radius: 80px;
    background: ${colors.blue[90]};
  `}
`;

const BenefitText = styled(Typography.Body)`
  line-height: 1.2;
  text-align: center;
`;
